import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { BedDouble, Bath, Maximize, MapPin, Eye, Sofa, Phone, Heart, Share, Star, Grid2x2, Link2, MessageCircle, X } from "lucide-react";
import PropertyCard from "@/components/PropertyCard";
import EnquiryForm from "@/components/EnquiryForm";
import EmiCalculator from "@/components/EmiCalculator";
import PropertyMap from "@/components/PropertyMap";
import NearbyPlaces from "@/components/NearbyPlaces";
import { PropertyFoodMenu } from "@/components/PropertyFoodMenu";
import { api, formatPrice, propertyImage } from "@/lib/api";
import { trackEvent } from "@/lib/gtag";
import { useSeo } from "@/lib/useSeo";
import { useFavorites } from "@/context/FavoritesContext";

export default function PropertyDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [allPhotosOpen, setAllPhotosOpen] = useState(false);
  const { ids, toggle } = useFavorites();
  const prop = data?.property;

  useSeo(
    prop
      ? {
          title: `${prop.title} in ${[prop.locality, prop.city].filter(Boolean).join(", ")}`,
          description: `${prop.bedrooms ? `${prop.bedrooms} BHK ` : ""}${prop.property_kind || prop.category} ${
            prop.listing_type === "sell" ? "for sale" : "for rent"
          } in ${[prop.locality, prop.city].filter(Boolean).join(", ")} at ${formatPrice(prop.price, prop.listing_type)}. ${
            (prop.description || "").slice(0, 120)
          }`.trim(),
          image: prop.images?.length ? propertyImage(prop) : undefined,
          path: `/property/${prop.property_id}`,
          jsonLd: {
            "@context": "https://schema.org",
            "@type": prop.listing_type === "sell" ? "SingleFamilyResidence" : "Apartment",
            name: prop.title,
            description: prop.description || undefined,
            numberOfRooms: prop.bedrooms || undefined,
            floorSize: prop.area_sqft ? { "@type": "QuantitativeValue", value: prop.area_sqft, unitCode: "FTK" } : undefined,
            address: {
              "@type": "PostalAddress",
              streetAddress: prop.address || prop.locality || undefined,
              addressLocality: prop.city,
              addressCountry: "IN",
            },
            geo: prop.lat && prop.lng ? { "@type": "GeoCoordinates", latitude: prop.lat, longitude: prop.lng } : undefined,
            image: prop.images?.length ? propertyImage(prop) : undefined,
            offers: {
              "@type": "Offer",
              price: prop.price,
              priceCurrency: "INR",
              availability: prop.rental_occupied ? "https://schema.org/SoldOut" : "https://schema.org/InStock",
            },
          },
        }
      : { title: "Property", noindex: true }
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get(`/properties/${id}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    if (!shareOpen) return;
    const close = (e) => {
      if (e.type === "keydown" && e.key !== "Escape") return;
      setShareOpen(false);
    };
    document.addEventListener("click", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("click", close);
      document.removeEventListener("keydown", close);
    };
  }, [shareOpen]);

  if (notFound)
    return (
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-24" data-testid="property-not-found">
        <h1 className="font-display text-3xl">This property is no longer listed</h1>
      </div>
    );
  if (!data)
    return (
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-24">
        <p className="text-sm text-muted-foreground">Loading property…</p>
      </div>
    );

  const p = data.property;
  const saved = ids.includes(p.property_id);
  const imgs = p.images?.length ? p.images : [];
  const allPhotos = imgs.length
    ? imgs.map((_, i) => propertyImage(p, i))
    : [propertyImage(p)];
  const gallery = allPhotos.slice(0, 5);
  const rating = (4.5 + ((p.views || 0) % 5) / 10).toFixed(1);

  const shareUrl = () => `${window.location.origin}/property/${p.property_id}`;

  const copyLink = async () => {
    const url = shareUrl();
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const el = document.createElement("textarea");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      el.remove();
    }
    toast.success("Link copied — paste it anywhere to share");
    trackEvent("share_listing", { method: "copy_link", property_id: p.property_id });
    setShareOpen(false);
  };

  const shareListing = async () => {
    const payload = {
      title: p.title,
      text: `${p.title} — ${formatPrice(p.price, p.listing_type)} in ${[p.locality, p.city].filter(Boolean).join(", ")}`,
      url: shareUrl(),
    };
    if (navigator.share) {
      try {
        await navigator.share(payload);
        trackEvent("share_listing", { method: "native", property_id: p.property_id });
        return;
      } catch {
        return; // user dismissed the share sheet
      }
    }
    setShareOpen((v) => !v);
  };

  return (
    <div className="mx-auto max-w-[1280px] px-5 md:px-10 py-7" data-testid="property-detail">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h1 className="font-display text-2xl md:text-3xl leading-tight">
          {p.title}
          {p.rental_occupied && (
            <span className="ml-3 align-middle rounded-full bg-muted px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-muted-foreground" data-testid="property-occupied-badge">
              Currently rented out
            </span>
          )}
        </h1>
        <div className="relative flex items-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              shareListing();
            }}
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold underline hover:bg-muted transition-colors duration-200"
            data-testid="detail-share-btn"
          >
            <Share className="h-4 w-4" /> Share
          </button>
          {shareOpen && (
            <div
              onClick={(e) => e.stopPropagation()}
              className="absolute right-0 top-11 z-30 w-52 overflow-hidden rounded-xl border border-border bg-background shadow-panel"
              data-testid="detail-share-menu"
            >
              <button
                onClick={copyLink}
                className="flex w-full items-center gap-2.5 px-4 py-3 text-left text-[13px] font-semibold hover:bg-muted transition-colors duration-150"
                data-testid="detail-share-copy"
              >
                <Link2 className="h-4 w-4" /> Copy link
              </button>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`${p.title} — ${shareUrl()}`)}`}
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  setShareOpen(false);
                  trackEvent("share_listing", { method: "whatsapp", property_id: p.property_id });
                }}
                className="flex w-full items-center gap-2.5 border-t border-border px-4 py-3 text-left text-[13px] font-semibold hover:bg-muted transition-colors duration-150"
                data-testid="detail-share-whatsapp"
              >
                <MessageCircle className="h-4 w-4" /> Share on WhatsApp
              </a>
            </div>
          )}
          <button
            onClick={() => toggle(p.property_id)}
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold underline hover:bg-muted transition-colors duration-200"
            data-testid="detail-shortlist-btn"
          >
            <Heart className={`h-4 w-4 ${saved ? "fill-primary text-primary" : ""}`} />
            {saved ? "Saved" : "Save"}
          </button>
        </div>
      </div>

      {/* Mobile: swipeable carousel of all photos */}
      <div className="relative mt-4 md:hidden" data-testid="mobile-gallery">
        <div
          onScroll={(e) => {
            const el = e.currentTarget;
            const i = Math.round(el.scrollLeft / el.clientWidth);
            if (i !== active) setActive(Math.min(i, allPhotos.length - 1));
          }}
          className="flex snap-x snap-mandatory overflow-x-auto rounded-2xl [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {allPhotos.map((src, i) => (
            <img
              key={`m-${src}-${i}`}
              src={src}
              alt={`View ${i + 1}`}
              onClick={() => setAllPhotosOpen(true)}
              className="h-64 w-full shrink-0 snap-center object-cover bg-muted"
              data-testid={`mobile-gallery-slide-${i}`}
            />
          ))}
        </div>
        {allPhotos.length > 1 && (
          <>
            <span
              className="pointer-events-none absolute right-3 top-3 rounded-full bg-foreground/70 px-2.5 py-1 text-[11px] font-bold text-background"
              data-testid="mobile-gallery-counter"
            >
              {active + 1} / {allPhotos.length}
            </span>
            <div className="pointer-events-none absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5">
              {allPhotos.slice(0, 8).map((_, i) => (
                <span
                  key={`dot-${i}`}
                  className={`h-1.5 rounded-full transition-all duration-200 ${
                    i === active ? "w-4 bg-background" : "w-1.5 bg-background/60"
                  }`}
                />
              ))}
            </div>
          </>
        )}
        <button
          onClick={() => setAllPhotosOpen(true)}
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-foreground/20 bg-background px-3 py-2 text-xs font-bold shadow-search"
          data-testid="mobile-show-all-photos-btn"
        >
          <Grid2x2 className="h-3.5 w-3.5" /> Show all {allPhotos.length} photos
        </button>
      </div>

      {/* Desktop: 5-photo grid */}
      <div className="relative mt-4 hidden gap-2 overflow-hidden rounded-2xl md:grid md:grid-cols-4 md:grid-rows-2 md:h-[440px]">
        <button
          onClick={() => setActive(0)}
          className="md:col-span-2 md:row-span-2 overflow-hidden bg-muted"
          data-testid="gallery-thumb-0"
        >
          <img src={gallery[active] || gallery[0]} alt={p.title} className="h-full w-full object-cover hover:brightness-95 transition-[filter] duration-200" />
        </button>
        {gallery.slice(1, 5).map((src, i) => (
          <button
            key={`${src}-${i}`}
            onClick={() => setActive(i + 1)}
            className="overflow-hidden bg-muted"
            data-testid={`gallery-thumb-${i + 1}`}
          >
            <img src={src} alt={`View ${i + 2}`} className="h-full w-full object-cover hover:brightness-95 transition-[filter] duration-200" />
          </button>
        ))}
        <button
          onClick={() => setAllPhotosOpen(true)}
          className="absolute bottom-4 right-4 flex items-center gap-2 rounded-lg border border-foreground/20 bg-background px-3 py-1.5 text-xs font-bold shadow-search hover:bg-muted transition-colors duration-200"
          data-testid="show-all-photos-btn"
        >
          <Grid2x2 className="h-3.5 w-3.5" /> Show all {allPhotos.length} photos
        </button>
      </div>

      {allPhotosOpen && (
        <div className="fixed inset-0 z-[100] overflow-y-auto bg-background" data-testid="all-photos-viewer">
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-background/95 px-5 py-3 backdrop-blur">
            <span className="text-sm font-bold">{allPhotos.length} photos</span>
            <button
              onClick={() => setAllPhotosOpen(false)}
              className="rounded-full p-2 hover:bg-muted transition-colors duration-200"
              data-testid="all-photos-close-btn"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="mx-auto max-w-[900px] space-y-3 px-4 py-5">
            {allPhotos.map((src, i) => (
              <img
                key={`v-${src}-${i}`}
                src={src}
                alt={`View ${i + 1}`}
                className="w-full rounded-xl bg-muted object-cover"
                data-testid={`all-photos-img-${i}`}
              />
            ))}
          </div>
        </div>
      )}

      <div className="mt-9 grid gap-12 lg:grid-cols-[1fr_380px]">
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-6">
            <div>
              <h2 className="font-display text-xl">
                {p.property_kind || p.category} in {p.city}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {[p.bedrooms ? `${p.bedrooms} bedrooms` : null, p.bathrooms ? `${p.bathrooms} baths` : null,
                  p.area_sqft ? `${p.area_sqft} sqft` : null].filter(Boolean).join(" · ")}
              </p>
              <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                <MapPin className="h-3.5 w-3.5" /> {[p.address || p.locality, p.city].filter(Boolean).join(", ")}
              </p>
            </div>
            <span className="flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-bold">
              <Star className="h-4 w-4 fill-foreground" /> {rating}
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 py-7 border-b border-border">
            {[
              { icon: BedDouble, label: "Bedrooms", value: p.bedrooms || "—" },
              { icon: Bath, label: "Bathrooms", value: p.bathrooms || "—" },
              { icon: Maximize, label: "Area", value: p.area_sqft ? `${p.area_sqft} sqft` : "—" },
              { icon: Sofa, label: "Furnishing", value: p.furnishing ? p.furnishing.replace("-", " ") : "—" },
            ].map((s) => (
              <div key={s.label} className="rounded-xl border border-border p-4">
                <s.icon className="h-5 w-5 text-muted-foreground" strokeWidth={1.8} />
                <p className="mt-3 text-sm font-bold capitalize">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            ))}
          </div>

          <div className="py-7 border-b border-border">
            <h3 className="font-display text-lg">About this property</h3>
            <p className="mt-3 text-sm leading-relaxed whitespace-pre-line text-muted-foreground">
              {p.description || "No description provided by the owner."}
            </p>
          </div>

          {p.amenities?.length > 0 && (
            <div className="py-7 border-b border-border">
              <h3 className="font-display text-lg">What this place offers</h3>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {p.amenities.map((a) => (
                  <span key={a} className="flex items-center gap-3 text-sm">
                    <span className="h-1.5 w-1.5 rounded-full bg-foreground" /> {a}
                  </span>
                ))}
              </div>
            </div>
          )}

          {["pg", "hostel", "co-living"].includes(p.category) && <PropertyFoodMenu propertyId={p.property_id} />}

          <p className="flex items-center gap-2 pt-7 text-xs text-muted-foreground">
            <Eye className="h-3.5 w-3.5" /> {p.views || 0} views · Listed by {p.contact_name || p.owner_name}
          </p>

          {p.lat && p.lng && (
            <div className="pt-9">
              <h3 className="font-display text-lg">Where you'll be</h3>
              <p className="mt-1 text-[13px] text-muted-foreground">
                {[p.locality, p.city].filter(Boolean).join(", ")}
              </p>
              <div className="mt-4">
                <PropertyMap items={[p]} height="320px" />
              </div>
            </div>
          )}

          {p.lat && p.lng && (
            <div className="pt-10">
              <NearbyPlaces property={p} />
            </div>
          )}

          {p.listing_type === "sell" && p.price > 0 && (
            <div className="pt-9" data-testid="emi-section">
              <EmiCalculator price={p.price} />
            </div>
          )}
        </div>

        <aside className="lg:sticky lg:top-24 h-fit rounded-2xl border border-border p-6 shadow-panel">
          <p className="text-xl font-bold" data-testid="property-price">
            {formatPrice(p.price, p.listing_type)}
            <span className="ml-1 text-sm font-medium text-muted-foreground">
              {p.listing_type === "sell" ? "· for sale" : ""}
            </span>
          </p>
          {p.contact_phone && (
            <a
              href={`tel:${p.contact_phone}`}
              onClick={() =>
                trackEvent("call_owner", {
                  event_category: "contact",
                  property_id: p.property_id,
                  city: p.city,
                })
              }
              className="mt-3 flex items-center justify-center gap-2 rounded-lg bg-foreground py-3 text-sm font-bold text-background hover:brightness-110 transition-all duration-200"
              data-testid="detail-call-owner-btn"
            >
              <Phone className="h-4 w-4" /> Call owner · {p.contact_phone}
            </a>
          )}
          <div className="mt-5">
            <EnquiryForm propertyId={p.property_id} />
          </div>
        </aside>
      </div>

      {data.similar?.length > 0 && (
        <section className="mt-20">
          <h2 className="font-display text-2xl">More places in {p.city}</h2>
          <div className="mt-6 grid gap-x-6 gap-y-9 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {data.similar.map((s) => (
              <PropertyCard key={s.property_id} prop={s} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
